import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Disclosure } from '@headlessui/react';
import { getFullUrl } from '../../utils/utils';
import parse from 'html-react-parser';
import { formatBrandName, supportHostnameFor, lowerCaseBrandName, FOOTER_LOGOS } from '../../utils/utils';
import { getSocialLinks, SOCIAL_ICONS } from '../../utils/socialLinks';

const FooterLinkGroup = ({ title, children }) => (
  <>
    {/* Desktop: plain column */}
    <div className="hidden lg:flex flex-col gap-4 w-full">
      <h3 className="text-white font-semibold">{title}</h3>
      <ul className="list-none flex flex-col gap-2 p-0 m-0">
        {children}
      </ul>
    </div>
    {/* Mobile: accordion */}
    <Disclosure as="div" className="lg:hidden w-full border-b border-white">
      {({ open }) => (
        <>
          <Disclosure.Button className={`
            flex w-full justify-between items-center
            py-4 px-0 text-white font-semibold text-base
            bg-transparent border-0 cursor-pointer
          `}>
            <span>{title}</span>
            <svg
              width="20" height="20" viewBox="0 0 20 20"
              fill="none" className="text-white"
            >
              {open ? (
                <path d="M4 10h12" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" />
              ) : (
                <path d="M10 4v12M4 10h12" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" />
              )}
            </svg>
          </Disclosure.Button>
          <Disclosure.Panel className="pb-4">
            <ul className="list-none flex flex-col gap-2 p-0 m-0">
              {children}
            </ul>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  </>
);

const SUPPORT_BASE_URL = 'https://support';
const SHIPPLAY_PARTNER_URL = 'https://support.shipplay.com/hc/en-us/articles/18737832414996-Becoming-a-partner';
const SHIPSTICKS_PARTNER_URL = 'https://shipsticks.com/partnership-benefits';

const Footer = ({
  bgColor = '#1C2938', header, body, children, brand, shipLinkPath, subdomain,
  supportPhoneNumber = '(855) 792-7529',
}) => {
  function getSupportLink(path) {
    const isProductionEnv = window.production_env === 'true';
    if (!isProductionEnv) return getFullUrl(path);

    if (path === '/partners') {
      return brand === 'shipplay' ? SHIPPLAY_PARTNER_URL : SHIPSTICKS_PARTNER_URL;
    }

    return `${SUPPORT_BASE_URL}.${brand}.com`;
  }

  useEffect(() => {
    console.log('Current brand:', brand);
    console.log('Brand name:', formatBrandName(brand));
    console.log('Lower case brand name:', lowerCaseBrandName(brand));
    console.log('Resolved logo:', FOOTER_LOGOS[lowerCaseBrandName(brand)]);
  }, []);

  const resolvedLogo = FOOTER_LOGOS[lowerCaseBrandName(brand)];
  const brandName = formatBrandName(brand);
  const supportHostname = supportHostnameFor(brand);
  const linkClass = 'text-white text-sm hover:text-white';
  const legalLinkClass = 'text-white text-xs hover:text-white';

  return (
    <footer
      className="text-white px-6 py-8 sm:p-8 lg:px-14 xl:px-36 2xl:px-56 font-poppins"
      style={{ backgroundColor: bgColor }}
    >
      <div className="max-w-7xl mx-auto">
        {header && body && (
          <div className="text-left pb-8 lg:py-8 pl-[5%] lg:pl-0 gap-12">
            <h5 className="text-white text-xl font-semibold mb-2">{parse(header)}</h5>
            <p className="mb-2 text-lg leading-8">{parse(body)}</p>
          </div>
        )}
        {children}
        {/* Logo + Social Links */}
        <div className={`
          flex flex-col lg:flex-row justify-between items-center
          gap-x-12 gap-y-6 mb-12 py-8
          ${header && body ? 'border-t border-[#FFF] border-opacity-20' : ''}
        `}>
          <a href={getFullUrl('/', subdomain)}>
            <img
              src={resolvedLogo}
              alt="Footer Logo"
              className="w-[14rem] xl:w-[261px]"
            />
          </a>
          <ul className="list-none flex items-center gap-6 p-0 m-0">
            {Object.entries(SOCIAL_ICONS).map(([key, { label, path }]) => {
              const href = getSocialLinks(brand)[key];
              if (!href) return null;
              return (
                <li key={key}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className="text-white hover:text-white"
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d={path} />
                    </svg>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Primary Links */}
        <div className={`
          flex flex-col lg:flex-row justify-between items-start
          gap-x-[67px] gap-y-2 w-full lg:pb-6 lg:border-b
          border-neutral-400 mb-10 md:mb-12 lg:mb-10
        `}>
          <FooterLinkGroup title={brandName}>
            <li><a href={getFullUrl('/', subdomain)} className={linkClass}>Home</a></li>
            <li><a href={getFullUrl(shipLinkPath, subdomain)} className={linkClass}>Ship</a></li>
            <li><a href={getFullUrl('/track', subdomain)} className={linkClass}>Track</a></li>
            <li><a href={getFullUrl('/gift-cards', subdomain)} className={linkClass}>Gift Cards</a></li>
          </FooterLinkGroup>

          <FooterLinkGroup title="Help">
            <li><a href={getFullUrl('/how-it-works', subdomain)} className={linkClass}>How it works</a></li>
            <li>
              <a href={getSupportLink('/support')} target="_blank" rel="noreferrer" className={linkClass}>
                Support
              </a>
            </li>
            <li><a href={getFullUrl('/claims', subdomain)} className={linkClass}>Claims</a></li>
            <li><a href={getFullUrl('/store_locator', subdomain)} className={linkClass}>Drop-off locator</a></li>
          </FooterLinkGroup>

          <FooterLinkGroup title="Partner with us">
            <li>
              <a href={getSupportLink('/partners')} target="_blank" rel="noreferrer" className={linkClass}>
                Become a partner
              </a>
            </li>
          </FooterLinkGroup>

          <FooterLinkGroup title="About us">
            <li><a href={getFullUrl('/about', subdomain)} className={linkClass}>About Us</a></li>
            <li><a href={getFullUrl('/contact', subdomain)} className={linkClass}>Contact Us</a></li>
          </FooterLinkGroup>

          <FooterLinkGroup title="Get in touch">
            <li className="text-white text-sm">
              11400 N. Jog Rd, Suite 300<br />
              Palm Beach Gardens<br />Florida 33418
            </li>
            <li>
              <a className={linkClass} href={`tel:${supportPhoneNumber.replace(/\D/g, '')}`}>
                {supportPhoneNumber}
              </a>
            </li>
            <li>
              <a className={linkClass} href={`mailto:info@${supportHostname}`}>
                {`info@${supportHostname}`}
              </a>
            </li>
          </FooterLinkGroup>
        </div>

        {/* Secondary Links */}
        <div className="flex md:justify-center lg:justify-start flex-col md:flex-row md:gap-x-6 lg:gap-x-12 gap-y-4">
          <a href={getFullUrl('/terms', subdomain)} className={legalLinkClass}>Terms of Use</a>
          <a href={getFullUrl('/privacy', subdomain)} className={legalLinkClass}>Privacy Policy</a>
          <a href={getFullUrl('/cookie-policy', subdomain)} className={legalLinkClass}>Cookie Policy</a>
          <a href={getFullUrl('/usage-agreement', subdomain)} className={legalLinkClass}>Usage Right</a>
          <a href={getSupportLink('/support')} target="_blank" rel="noreferrer" className={legalLinkClass}>Support</a>
          <a href={getFullUrl('/sitemap', subdomain)} className={legalLinkClass}>Site Map</a>
        </div>
      </div>
    </footer>
  );
};

Footer.propTypes = {
  header: PropTypes.string,
  body: PropTypes.string,
  bgColor: PropTypes.string,
  brand: PropTypes.string,
  children: PropTypes.node,
  shipLinkPath: PropTypes.string.isRequired,
  subdomain: PropTypes.string,
  supportPhoneNumber: PropTypes.string,
};

export default Footer;
